import { Fragment, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "../lib/api";
import { Handshake, ChevronDown, ChevronRight } from "lucide-react";

// Модерация заявок в партнёры: воронка new → contacted → interviewed → training → active / rejected.
// Бэкенд: server/admin/routes/partner-applications.ts. Заявки приходят с /for-partners.

interface PartnerApplication {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  telegram: string | null;
  city: string | null;
  experience: string | null;
  niches: string[] | null;
  monthlyClientsGoal: string | null;
  plan: string | null;
  hasClientBase: boolean | null;
  source: string | null;
  notes: string | null;
  status: string;
  createdAt: string | null;
}

const STATUSES = [
  { value: "new", label: "Новая", className: "bg-blue-100 text-blue-700" },
  { value: "contacted", label: "Связались", className: "bg-amber-100 text-amber-700" },
  { value: "interviewed", label: "Интервью", className: "bg-purple-100 text-purple-700" },
  { value: "training", label: "Обучение", className: "bg-cyan-100 text-cyan-700" },
  { value: "active", label: "Активен", className: "bg-green-100 text-green-700" },
  { value: "rejected", label: "Отказ", className: "bg-gray-200 text-gray-600" },
] as const;

const NICHE_LABELS: Record<string, string> = {
  cleaning: "Клининг",
  electronics: "Электроника",
  "driving-schools": "Автошколы",
  beauty: "Красота/салоны",
  medicine: "Медицина",
  education: "Образование",
  ecommerce: "E-commerce",
  realestate: "Недвижимость",
  other: "Другое",
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUSES.find((s) => s.value === status) ?? STATUSES[0];
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function PartnerApplicationsPage() {
  const [filter, setFilter] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState("");

  const applicationsQuery = useQuery<PartnerApplication[]>({
    queryKey: ["/api/admin/partner-applications"],
  });
  const applications = applicationsQuery.data ?? [];
  const visible = filter ? applications.filter((a) => a.status === filter) : applications;
  const countOf = (status: string) => applications.filter((a) => a.status === status).length;

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { status?: string; notes?: string } }) =>
      apiRequest("PATCH", `/api/admin/partner-applications/${id}`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/partner-applications"] }),
  });

  function toggleExpand(app: PartnerApplication) {
    if (expandedId === app.id) {
      setExpandedId(null);
    } else {
      setExpandedId(app.id);
      setNotesDraft(app.notes ?? "");
    }
  }

  const chip = (active: boolean) =>
    `px-3 py-1 rounded-full text-sm font-medium transition-colors ${
      active ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-accent"
    }`;

  return (
    <div className="max-w-6xl">
      <h1 className="flex items-center gap-2 text-2xl font-semibold mb-1">
        <Handshake className="w-6 h-6 text-primary" />
        Заявки партнёров
      </h1>
      <p className="text-sm text-muted-foreground mb-4">
        Заявки со страницы «Стать партнёром». Ведите воронку сменой статуса.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        <button className={chip(filter === null)} onClick={() => setFilter(null)}>
          Все ({applications.length})
        </button>
        {STATUSES.map((s) => (
          <button key={s.value} className={chip(filter === s.value)} onClick={() => setFilter(s.value)}>
            {s.label} ({countOf(s.value)})
          </button>
        ))}
      </div>

      {update.isError && (
        <p className="text-sm text-destructive mb-3">
          Не удалось сохранить: {(update.error as Error).message}
        </p>
      )}

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="px-4 py-2 w-8"></th>
              <th className="px-4 py-2">Дата</th>
              <th className="px-4 py-2">Имя</th>
              <th className="px-4 py-2">Контакты</th>
              <th className="px-4 py-2">Город</th>
              <th className="px-4 py-2">Источник</th>
              <th className="px-4 py-2">Цель, клиентов/мес</th>
              <th className="px-4 py-2">Статус</th>
            </tr>
          </thead>
          <tbody>
            {applicationsQuery.isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  Загрузка…
                </td>
              </tr>
            )}
            {!applicationsQuery.isLoading && visible.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  {filter ? "Нет заявок с этим статусом" : "Заявок пока нет"}
                </td>
              </tr>
            )}
            {visible.map((app) => (
              <Fragment key={app.id}>
                <tr
                  className="border-b border-border last:border-b-0 hover:bg-accent/50 cursor-pointer"
                  onClick={() => toggleExpand(app)}
                >
                  <td className="px-4 py-2 text-muted-foreground">
                    {expandedId === app.id ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatDate(app.createdAt)}</td>
                  <td className="px-4 py-2 font-medium">{app.name}</td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col gap-0.5">
                      {app.telegram && (
                        <a
                          className="text-primary hover:underline"
                          href={`https://t.me/${app.telegram.replace(/^@/, "")}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {app.telegram}
                        </a>
                      )}
                      {app.phone && (
                        <a className="text-primary hover:underline" href={`tel:${app.phone.replace(/[^+\d]/g, "")}`}>
                          {app.phone}
                        </a>
                      )}
                      {app.email && (
                        <a className="text-primary hover:underline" href={`mailto:${app.email}`}>
                          {app.email}
                        </a>
                      )}
                      {!app.telegram && !app.phone && !app.email && "—"}
                    </div>
                  </td>
                  <td className="px-4 py-2">{app.city || "—"}</td>
                  <td className="px-4 py-2">{app.source || "—"}</td>
                  <td className="px-4 py-2">{app.monthlyClientsGoal || "—"}</td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={app.status} />
                      <select
                        className="px-2 py-1 rounded-md border border-border bg-background text-xs"
                        value={app.status}
                        disabled={update.isPending}
                        onChange={(e) => update.mutate({ id: app.id, data: { status: e.target.value } })}
                      >
                        {STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
                {expandedId === app.id && (
                  <tr className="border-b border-border bg-accent/30">
                    <td colSpan={8} className="px-4 py-3">
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Ниши: </span>
                            {app.niches?.length
                              ? app.niches.map((n) => NICHE_LABELS[n] || n).join(", ")
                              : "—"}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Своя база клиентов: </span>
                            {app.hasClientBase ? "да" : "нет"}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Опыт: </span>
                            {app.experience || "—"}
                          </div>
                          <div>
                            <span className="text-muted-foreground">План привлечения: </span>
                            {app.plan || "—"}
                          </div>
                        </div>
                        <div>
                          <label className="block text-muted-foreground text-sm mb-1">Заметки</label>
                          <textarea
                            className="w-full px-2 py-1.5 rounded-md border border-border bg-background text-sm min-h-20"
                            value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)}
                          />
                          <button
                            className="mt-2 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground disabled:opacity-50"
                            disabled={update.isPending || notesDraft === (app.notes ?? "")}
                            onClick={() => update.mutate({ id: app.id, data: { notes: notesDraft } })}
                          >
                            Сохранить заметки
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
